import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { User } from '@/schema'

interface ViewerAuditStore {
  // State
  viewersByDocId: Record<string, User[]>
  isLoadingByDocId: Record<string, boolean>

  // Actions
  setViewers: (
    documentId: string,
    versionId: string | null,
    viewers: User[]
  ) => void
  setLoading: (
    documentId: string,
    versionId: string | null,
    isLoading: boolean
  ) => void
  getViewers: (documentId: string, versionId: string | null) => User[]
  getIsLoading: (documentId: string, versionId: string | null) => boolean
  reset: () => void
}

const getCompositeKey = (documentId: string, versionId: string | null) =>
  versionId ? `${documentId}::${versionId}` : documentId

const initialState = {
  viewersByDocId: {},
  isLoadingByDocId: {},
}

export const useViewerAuditStore = create<ViewerAuditStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setViewers: (
        documentId: string,
        versionId: string | null,
        viewers: User[]
      ) => {
        const key = getCompositeKey(documentId, versionId)
        set(
          (state) => ({
            viewersByDocId: {
              ...state.viewersByDocId,
              [key]: viewers,
            },
          }),
          false,
          'setViewers'
        )
      },

      setLoading: (
        documentId: string,
        versionId: string | null,
        isLoading: boolean
      ) => {
        const key = getCompositeKey(documentId, versionId)
        set(
          (state) => ({
            isLoadingByDocId: {
              ...state.isLoadingByDocId,
              [key]: isLoading,
            },
          }),
          false,
          'setLoading'
        )
      },

      getViewers: (documentId: string, versionId: string | null) => {
        const key = getCompositeKey(documentId, versionId)
        return get().viewersByDocId[key] || []
      },

      getIsLoading: (documentId: string, versionId: string | null) => {
        const key = getCompositeKey(documentId, versionId)
        return get().isLoadingByDocId[key] || false
      },

      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    { name: 'ViewerAuditStore' }
  )
)
